/**
 * BattleCode Demo v1.0.0 — Modal Helper
 *
 * P1-#6 修复（2026-07-04）：
 *   之前屏 4 Give Up 用 `window.confirm()`,但 WebView / iOS Safari 上被吞或无响应。
 *   现在用内嵌 DOM modal + Promise 异步返回布尔。
 *
 * 设计取舍：
 *   - 单文件 helper,不依赖框架(纯 Vanilla TS)
 *   - 每次调用都创建独立的 modal 节点,关闭后 destroy(避免 DOM 泄漏)
 *   - 背景遮罩点击 = 取消(与原生 confirm 一致)
 *   - ESC 键 = 取消
 *   - 焦点自动落到"确认"按钮(键盘可达性)
 *
 * 后续 v1+ 可考虑：
 *   - 复用 modal 节点(创建 1 次,show/hide 切换)
 *   - 加 ARIA role="dialog" / focus trap(无障碍合规)
 *   - 支持多按钮(Yes/No/Cancel 3 选项)
 */

/**
 * 显示内嵌确认弹窗(替代 window.confirm)
 * @param title 主标题
 * @param message 描述
 * @param confirmText 确认按钮文案(默认 "确认")
 * @param cancelText 取消按钮文案(默认 "取消")
 * @returns Promise<boolean> 确认 → true, 取消 → false
 */
export function showConfirmModal(
  title: string,
  message: string,
  confirmText: string = "OK",
  cancelText: string = "Cancel",
): Promise<boolean> {
  return new Promise((resolve) => {
    // 1) 创建遮罩
    const overlay = document.createElement("div");
    overlay.className = "modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "modal-title");

    // 2) 创建弹窗
    const modal = document.createElement("div");
    modal.className = "modal-dialog";

    const titleEl = document.createElement("h2");
    titleEl.id = "modal-title";
    titleEl.className = "modal-title";
    titleEl.textContent = title;

    const msgEl = document.createElement("p");
    msgEl.className = "modal-message";
    msgEl.textContent = message;

    const actions = document.createElement("div");
    actions.className = "modal-actions";

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "btn btn-ghost modal-cancel";
    cancelBtn.textContent = cancelText;

    const confirmBtn = document.createElement("button");
    confirmBtn.className = "btn btn-danger modal-confirm";
    confirmBtn.textContent = confirmText;

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    modal.appendChild(titleEl);
    modal.appendChild(msgEl);
    modal.appendChild(actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // 3) 关闭函数
    function close(result: boolean) {
      window.removeEventListener("keydown", onKeydown);
      document.removeEventListener("keydown", onKeydown, true);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      resolve(result);
    }

    // 4) 事件
    cancelBtn.addEventListener("click", () => close(false));
    confirmBtn.addEventListener("click", () => close(true));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close(false); // 点遮罩 = 取消
    });

    function onKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") close(false);
      else if (e.key === "Enter" && document.activeElement === confirmBtn) close(true);
    }
    window.addEventListener("keydown", onKeydown);

    // 5) 焦点
    setTimeout(() => confirmBtn.focus(), 0);
  });
}
